// Runtime ICE server configuration.
//
// Instead of hardcoding TURN credentials in the bundle (where they'd be scraped
// and abused), the app fetches short-lived credentials at runtime from our
// Cloudflare Worker (see turn-worker/). The worker returns a ready-to-use
// `iceServers` array plus a `ttl` so we know when to refresh.
//
// Everything here degrades gracefully: if the worker is unreachable we fall back
// to the last cached credentials, and failing that to STUN-only (direct P2P
// still works on most home networks; only symmetric-NAT/cellular needs relay).

import { track, EVENTS } from './analytics.js';
import { getStoredJSON, setStoredJSON } from './utils.js';

const CACHE_KEY = 'cammirror:ice';
const REFRESH_MARGIN_MS = 5 * 60 * 1000; // refresh this long before expiry
const FETCH_TIMEOUT_MS = 6000;

// Used when we have no credentials at all. Cloudflare + Google STUN for
// redundancy. No TURN here — a relay requires credentials.
const STUN_FALLBACK = [
  { urls: ['stun:stun.cloudflare.com:3478', 'stun:stun.l.google.com:19302'] },
];

function credentialsUrl() {
  const meta = document.querySelector('meta[name="turn-credentials-url"]');
  const fromMeta = meta && meta.content && meta.content.trim();
  // Priority: runtime override > <meta> tag (set to the deployed Worker URL in
  // production) > local-dev default > same-origin path (for a Pages Function
  // co-hosted with the app).
  if (window.CAMMIRROR_TURN_URL) return window.CAMMIRROR_TURN_URL;
  if (fromMeta) return fromMeta;
  // Dev convenience: `npm run dev` in turn-worker/ serves the minter on :8787,
  // so a locally-served app uses real TURN with zero extra config. This never
  // applies in production (hostname won't be localhost, and meta will be set).
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1') {
    return 'http://localhost:8787/turn-credentials';
  }
  return '/turn-credentials';
}

function readCache() {
  const parsed = getStoredJSON(CACHE_KEY);
  if (!parsed || !Array.isArray(parsed.iceServers) || !parsed.expiresAt) return null;
  return parsed;
}

function writeCache(iceServers, expiresAt) {
  setStoredJSON(CACHE_KEY, { iceServers, expiresAt });
}

export function iceCacheExpiresAt() {
  const cached = readCache();
  return cached ? cached.expiresAt : 0;
}

function withTimeout(promise, ms) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  return { signal: controller.signal, done: () => clearTimeout(timer) };
}

// Fetch fresh ICE servers, using the cache unless it's near expiry (or forced).
export async function fetchIceServers({ force = false } = {}) {
  const now = Date.now();
  const cached = readCache();
  if (!force && cached && cached.expiresAt > now + REFRESH_MARGIN_MS) {
    return cached.iceServers;
  }

  const t = withTimeout(null, FETCH_TIMEOUT_MS);
  try {
    const res = await fetch(credentialsUrl(), { method: 'GET', signal: t.signal });
    t.done();
    if (!res.ok) throw new Error('HTTP ' + res.status);
    const data = await res.json();
    const iceServers = Array.isArray(data.iceServers) ? data.iceServers : null;
    if (!iceServers || !iceServers.length) throw new Error('no iceServers in response');
    const ttlMs = (Number(data.ttl) || 24 * 3600) * 1000;
    writeCache(iceServers, now + ttlMs);
    return iceServers;
  } catch (err) {
    t.done();
    console.warn('[CAMMIRROR] TURN credential fetch failed, using fallback:', err && err.message);
    track(EVENTS.ICE_FALLBACK, { mode: cached ? 'stale' : 'stun_only' });
    // Stale credentials are better than none; STUN-only is the last resort.
    return cached ? cached.iceServers : STUN_FALLBACK;
  }
}

// --- Keeping a live peer's credentials fresh -------------------------------
// PeerJS reads `peer.options.config` when it builds each RTCPeerConnection, so
// refreshing credentials for future calls/reconnects is just a matter of
// swapping `iceServers` on that object before the next connection. Driving this
// centrally covers both the host and the client without touching either.

let refreshTimer = null;
let managedPeer = null;
let listenersBound = false;

function applyIceServers(peer, iceServers) {
  if (peer && peer.options && peer.options.config) {
    peer.options.config = { ...peer.options.config, iceServers };
  }
}

function scheduleNext() {
  clearTimeout(refreshTimer);
  const expiresAt = iceCacheExpiresAt();
  const now = Date.now();
  // With a cache: refresh a little before expiry. Without one (we're on the
  // STUN fallback): retry soon so we upgrade to a relay as soon as possible.
  const delay = expiresAt ? Math.max(30000, expiresAt - now - REFRESH_MARGIN_MS) : 60000;
  refreshTimer = setTimeout(runRefresh, delay);
}

async function runRefresh() {
  const iceServers = await fetchIceServers({ force: true });
  applyIceServers(managedPeer, iceServers);
  scheduleNext();
}

async function refreshIfStale() {
  const expiresAt = iceCacheExpiresAt();
  if (!expiresAt || expiresAt < Date.now() + REFRESH_MARGIN_MS) {
    await runRefresh();
  }
}

export function keepIceFresh(peer) {
  managedPeer = peer;
  scheduleNext();
  if (!listenersBound) {
    listenersBound = true;
    // Coming back from background / regaining network is exactly when a long
    // idle session may have crossed the expiry boundary.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') refreshIfStale();
    });
    window.addEventListener('online', refreshIfStale);
  }
}
